import React, { useState } from 'react';
import { FadeInUp } from './FadeInUp';
import { CheckCircle2 } from 'lucide-react';
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  investment: string;
}
export function LeadForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    investment: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'El nombre es requerido';
    if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Correo electrónico inválido';
    }
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es requerido';
    if (!formData.investment) newErrors.investment = 'Selecciona una opción';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Simulate API call
      setTimeout(() => {
        setIsSubmitted(true);
      }, 500);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  return <section id="lead-form" className="bg-deep-blue py-24 relative overflow-hidden" data-id="element-42">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-43">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-44"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-vitality-green opacity-10 rounded-full blur-3xl" data-id="element-45"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-46">
        <FadeInUp data-id="element-47">
          <div className="text-center mb-12" data-id="element-48">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-49">
              Da el primer paso hacia tu tranquilidad financiera
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto" data-id="element-50">
              Deja tus datos para recibir una asesoría gratuita y personalizada.
              Descubre si el IUL es la solución perfecta para ti.
            </p>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-51">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-52">
            {isSubmitted ? <div className="text-center py-12" data-id="element-53">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6" data-id="element-54">
                  <CheckCircle2 className="w-10 h-10 text-vitality-green" data-id="element-55" />
                </div>
                <h3 className="text-2xl font-bold text-deep-blue mb-4" data-id="element-56">
                  ¡Gracias por solicitar tu asesoría!
                </h3>
                <p className="text-lg text-gray-600" data-id="element-57">
                  En breve uno de nuestros expertos te contactará para agendar
                  tu sesión gratuita.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-58">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-59">
                  <div data-id="element-60">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-61">
                      Nombre
                    </label>
                    <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-vitality-green focus:border-transparent outline-none transition-all`} placeholder="Tu nombre" data-id="element-62" />
                    {errors.firstName && <p className="mt-1 text-sm text-red-500" data-id="element-63">
                        {errors.firstName}
                      </p>}
                  </div>
                  <div data-id="element-64">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-65">
                      Apellido
                    </label>
                    <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-vitality-green focus:border-transparent outline-none transition-all`} placeholder="Tu apellido" data-id="element-66" />
                    {errors.lastName && <p className="mt-1 text-sm text-red-500" data-id="element-67">
                        {errors.lastName}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-68">
                  <div data-id="element-69">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-70">
                      Correo electrónico
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-vitality-green focus:border-transparent outline-none transition-all`} placeholder="tu@correo.com" data-id="element-71" />
                    {errors.email && <p className="mt-1 text-sm text-red-500" data-id="element-72">
                        {errors.email}
                      </p>}
                  </div>
                  <div data-id="element-73">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-74">
                      Teléfono
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-vitality-green focus:border-transparent outline-none transition-all`} placeholder="(555) 123-4567" data-id="element-75" />
                    {errors.phone && <p className="mt-1 text-sm text-red-500" data-id="element-76">
                        {errors.phone}
                      </p>}
                  </div>
                </div>

                <div data-id="element-77">
                  <label htmlFor="investment" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-78">
                    ¿Qué monto estarías dispuesto a invertir mensualmente?
                  </label>
                  <select id="investment" name="investment" value={formData.investment} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg border ${errors.investment ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-vitality-green focus:border-transparent outline-none transition-all bg-white`} data-id="element-79">
                    <option value="" data-id="element-80">Selecciona una opción</option>
                    <option value="150-300" data-id="element-81">$150 - $300</option>
                    <option value="300-500" data-id="element-82">$300 - $500</option>
                    <option value="500-800" data-id="element-83">$500 - $800</option>
                    <option value="800+" data-id="element-84">Más de $800</option>
                  </select>
                  {errors.investment && <p className="mt-1 text-sm text-red-500" data-id="element-85">
                      {errors.investment}
                    </p>}
                </div>

                <button type="submit" className="w-full bg-vitality-green hover:bg-vitality-green-hover text-white font-bold text-lg py-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg mt-4" data-id="element-86">
                  Quiero mi asesoría gratuita
                </button>

                <p className="text-xs text-center text-gray-500 mt-6" data-id="element-87">
                  Al dejar tus datos, recibirás una asesoría personalizada para
                  saber si el IUL es el producto ideal para ti y tu familia.
                </p>
              </form>}
          </div>
        </FadeInUp>
      </div>
    </section>;
}